import { useRef, useState } from 'react';
import { useSiteContent } from '@/__lib/SiteContentProvider';
import { RESERVATION_URL } from '@/content/reservation';
import { strings } from '@/i18n/strings';
import { Button } from '@components/__shared/Button/Button';
import { CtaBand } from '@components/__shared/CtaBand/CtaBand';
import { Logo } from '@components/__shared/Logo/Logo';
import { Photo } from '@components/__shared/Photo/Photo';
import { RichText, hasRichTextContent } from '@components/__shared/RichText/RichText';
import { Seo } from '@components/__shared/Seo/Seo';
import { StarRating } from '@components/__shared/StarRating/StarRating';
import { RecommendationDialog } from './RecommendationDialog';
import trainer1Photo from './images/trainer_1.jpg';
import trainer2Photo from './images/trainer_2.jpg';
import styles from './Home.module.scss';

export const Home = () => {
	const { siteMeta, homeContent, testimonials } = useSiteContent();
	const { hero, features, contraindications, howItWorks, trainerTeaser } = homeContent;
	const contraindicationItems = contraindications.filter(item => item.trim());
	const [dialogOpen, setDialogOpen] = useState(false);

	const heroRef = useRef<HTMLElement>(null);
	const autoScrolled = useRef(false);
	const revealHero = () => {
		if (autoScrolled.current || window.scrollY > 20) return;
		autoScrolled.current = true;
		heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	return (
		<>
			<Seo
				title={strings.home.seoTitle}
				description={strings.home.seoDescription(siteMeta.businessName, siteMeta.city)}
				path="/"
			/>

			<section className={styles.logoIntro} onAnimationEnd={revealHero}>
				<Logo onDark className={styles.introLogo} />
				<p className={styles.introTagline}>{strings.home.logoIntroTagline}</p>
			</section>

			<section ref={heroRef} className={styles.hero}>
				<div className={styles.heroText}>
					<p className={styles.eyebrow}>{hero.eyebrow}</p>
					<h1>{hero.headline}</h1>
					<RichText as="div" className={styles.subhead} html={hero.subhead} />
					<div className={styles.heroActions}>
						<Button variant="accent" href={RESERVATION_URL} target="_blank" rel="noreferrer">
							{hero.primaryCta.label}
						</Button>
						<Button to={hero.secondaryCta.to} variant="outline">
							{hero.secondaryCta.label}
						</Button>
					</div>
				</div>
				<Photo src="/images/studio-training.jpg" className={styles.heroPhoto} />
			</section>

			<section className={styles.features}>
				<div className={styles.featuresHead}>
					<p className={styles.featuresEyebrow}>{strings.home.featuresEyebrow}</p>
					<h2>{strings.home.featuresHeading}</h2>
				</div>
				<div className={styles.featuresGrid}>
					{features
						.filter(feature => feature.title.trim() || hasRichTextContent(feature.description))
						.map((feature, index) => (
							<div key={index} className={styles.featureCard}>
								<h3>{feature.title}</h3>
								<RichText className={styles.featureDescription} html={feature.description} />
							</div>
						))}
				</div>
			</section>

			{contraindicationItems.length > 0 && (
				<section className={styles.contraindications}>
					<div className={styles.contraindicationsHead}>
						<p className={styles.contraindicationsEyebrow}>{strings.home.contraindicationsEyebrow}</p>
						<h2>{strings.home.contraindicationsHeading}</h2>
					</div>
					<ul className={styles.contraindicationsList}>
						{contraindicationItems.map((item, index) => (
							<li key={index}>{item}</li>
						))}
					</ul>
				</section>
			)}

			<section className={styles.howItWorks}>
				<div className={styles.howItWorksHead}>
					<p className={styles.howItWorksEyebrow}>{strings.home.howItWorksEyebrow}</p>
					<h2>{howItWorks.headline}</h2>
				</div>
				<ol className={styles.steps}>
					{howItWorks.steps.map((step, index) => (
						<li key={index} className={styles.step}>
							<div className={styles.stepHead}>
								<span className={styles.stepNumber}>{index + 1}</span>
								<h3>{step.title}</h3>
							</div>
							<RichText className={styles.stepDescription} html={step.description} />
						</li>
					))}
				</ol>
			</section>

			<section className={styles.trainerTeaser}>
				<div>
					<h2>{trainerTeaser.headline}</h2>
					<RichText className={styles.blurb} html={trainerTeaser.blurb} />
					<Button className={styles.trainerTeaserCta} to={trainerTeaser.cta.to}>
						{trainerTeaser.cta.label}
					</Button>
				</div>
				<div className={styles.trainerPhotos}>
					<Photo portrait src={trainer1Photo} alt={strings.home.trainerPhotoAlt} />
					<Photo portrait src={trainer2Photo} alt={strings.home.trainerkaPhotoAlt} />
				</div>
			</section>

			<section className={styles.testimonials}>
				<div className={styles.testimonialsHeader}>
					<div>
						<p className={styles.testimonialsEyebrow}>{strings.home.testimonialsEyebrow}</p>
						<h2>{strings.home.testimonialsHeading}</h2>
					</div>
					<Button variant="outline" onClick={() => setDialogOpen(true)}>
						{strings.home.shareExperience}
					</Button>
				</div>

				{testimonials.length > 0 ? (
					<div className={styles.testimonialsGrid}>
						{testimonials.map((testimonial, index) => (
							<blockquote key={index} className={styles.testimonial}>
								<span className={styles.mark} aria-hidden="true">
									&ldquo;
								</span>
								<p className={styles.quote}>{testimonial.quote}</p>
								<footer className={styles.author}>
									<span className={styles.name}>{testimonial.name}</span>
									<StarRating value={testimonial.rating} />
								</footer>
							</blockquote>
						))}
					</div>
				) : (
					<p className={styles.testimonialsEmpty}>{strings.home.testimonialsEmpty}</p>
				)}
			</section>

			{dialogOpen && <RecommendationDialog onClose={() => setDialogOpen(false)} />}

			<CtaBand />
		</>
	);
};
